import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProgressDot from '../../shared/ProgressDot';
import { CategoryPill } from '../../shared/Pill';
import { formatNumber } from '../../shared/numberFormat';
import { formatDateTime } from '../../shared/tenantClock';
import '../../shared/ListRowHover.css';

// Warehouse › Lists › Fulfilments (issue #306) — one row for every unfinished order
// (not fully shipped), every order type together, oldest first: where it's
// up to, how much is Picked, Packed and Shipped, and how long since it was
// Accepted. Orders not yet Accepted show greyed as Awaiting Acceptance.
// Clicking a row opens the order at its current stage on Pick, Pack or
// Ship Order; an Awaiting Acceptance row opens the order's own page.
// The stage itself is worked out server-side (fulfilmentService.overview).
const STAGES = {
  awaiting_acceptance: { label: 'Awaiting Acceptance', color: '#8a94a6' },
  to_pick: { label: 'To Pick', color: '#1976d2' },
  picking: { label: 'Picking', color: '#6f42c1' },
  to_pack: { label: 'To Pack', color: '#c98a1f' },
  to_ship: { label: 'To Ship', color: '#1e7e34' },
  part_shipped: { label: 'Part Shipped', color: '#0e7c86' },
};

const STAGE_PATHS = { pick: '/warehouse-pick', pack: '/warehouse-pack', ship: '/warehouse-ship' };

function formatQty(n) {
  return formatNumber(Number(n || 0), { maxDecimals: 2 });
}

// Same none/partial/full dot as the Sales Orders list (ui-architecture.md
// "Fulfilment Progress Circle"), with the quantities in its tooltip.
function progress(total, done, verb) {
  const title = `${formatQty(done)} of ${formatQty(total)} ${verb}`;
  if (total <= 0 || done <= 0) return { state: 'empty', title };
  if (done >= total) return { state: 'full', title };
  return { state: 'partial', title };
}

// How long since the order was Accepted.
function sinceAccepted(acceptedAt) {
  if (!acceptedAt) return '—';
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(acceptedAt).getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} days`;
}

const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD = { padding: '6px 8px' };

function FulfilmentPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/fulfilment/overview')
      .then((res) => setRows(res.data.data || []))
      .catch((err) => setError(err.response?.data?.message || 'Could not load unfinished orders'))
      .finally(() => setLoading(false));
  }, []);

  const openRow = (row) => {
    if (!row.open_at) navigate(row.link);
    else navigate(STAGE_PATHS[row.open_at], { state: { orderType: row.order_type, orderId: row.id } });
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Fulfilments</h2>
      </div>

      <div style={{ margin: '18px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '13%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={TH}>Order #</th>
              <th style={TH}>Customer</th>
              <th style={TH}>Stage</th>
              <th style={{ ...TH, textAlign: 'center' }}>Picked</th>
              <th style={{ ...TH, textAlign: 'center' }}>Packed</th>
              <th style={{ ...TH, textAlign: 'center' }}>Shipped</th>
              <th style={TH}>Flags</th>
              <th style={{ ...TH, textAlign: 'right' }}>Accepted</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : error ? (
              <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14, color: '#b91c1c' }}>{error}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No unfinished orders</td></tr>
            ) : rows.map((row) => {
              const stage = STAGES[row.stage] || { label: row.stage };
              const awaiting = row.stage === 'awaiting_acceptance';
              const total = Number(row.qty_total || 0);
              return (
                <tr
                  key={`${row.order_type}-${row.id}`}
                  className="list-row"
                  onClick={() => openRow(row)}
                  style={{ cursor: 'pointer', opacity: awaiting ? 0.55 : 1 }}
                >
                  <td className="cell-title" style={{ ...TD, fontSize: 14, fontWeight: 600 }}>{row.display_number}</td>
                  <td data-label={row.party?.role || 'Customer'} style={{ ...TD, fontSize: 14 }}>{row.party?.name}</td>
                  <td data-label="Stage" style={TD}><CategoryPill value={stage.label} color={stage.color} /></td>
                  <td data-label="Picked" style={{ ...TD, textAlign: 'center' }}><ProgressDot {...progress(total, Number(row.qty_picked_total || 0), 'Picked')} /></td>
                  <td data-label="Packed" style={{ ...TD, textAlign: 'center' }}><ProgressDot {...progress(total, Number(row.qty_packed_total || 0), 'Packed')} /></td>
                  <td data-label="Shipped" style={{ ...TD, textAlign: 'center' }}><ProgressDot {...progress(total, Number(row.qty_shipped_total || 0), 'Shipped')} /></td>
                  <td data-label="Flags" style={TD}>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                      {row.on_hold && <CategoryPill value="On Hold" color="#9a6700" title={row.on_hold_reason || undefined} />}
                      {row.backordered && <CategoryPill value="Backordered" />}
                    </span>
                  </td>
                  <td
                    data-label="Accepted"
                    style={{ ...TD, textAlign: 'right', fontSize: 13, color: '#666' }}
                    title={row.accepted_at ? `Accepted ${formatDateTime(row.accepted_at)}` : undefined}
                  >
                    {sinceAccepted(row.accepted_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: '#666' }}>
          {formatNumber(rows.length, { decimals: 0 })} unfinished {rows.length === 1 ? 'order' : 'orders'}
        </div>
      )}
    </div>
  );
}

export default FulfilmentPage;
